import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { RootState } from '.';
import { getDailyProcessingDate } from '../lib/birthday';
import { createBirthdayNotifications } from '../lib/notificationCreator';
import { PersistentData, PersistentUserNotificationState } from '../lib/PersistentData';
import { persistUserNotificationState } from '../lib/storage';
import { LandProfile } from '../models/LandProfile';
import { Notification } from '../models/Notification';
import { UserState } from './user';

const blancoVersion = process.env.REACT_APP_VERSION as string;

export interface NotificationState {
  landProfile?: LandProfile;
  notifications: Notification[];
  createdAt?: number; // 通知が作成された時間(Unix time)
  unread: boolean; // 未読フラグ
  notify: boolean; // 通知カードを出すかどうか
  userNotificationStates?: { [userId: string]: PersistentUserNotificationState };
}

const initialState: NotificationState = {
  notifications: [],
  unread: false,
  notify: false
};

const slice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    persistentDataRestored: (state, { payload }: PayloadAction<PersistentData>) => {
      state.userNotificationStates = payload.userNotificationStates;
    },

    notificationNotified: state => {
      state.notify = false;
    },

    refreshNotifications: () => {
      /* do nothing */
    },

    notificationCenterOpened: state => {
      state.unread = false;
    },

    getIslandProfileSucceeded: (state, { payload }: PayloadAction<LandProfile>) => {
      state.landProfile = payload;
    },

    notificationsCreated: (
      state,
      {
        payload
      }: PayloadAction<{
        items: Notification[];
        createdAt: number;
        unread: boolean;
        notify: boolean;
      }>
    ) => {
      state.notifications = payload.items;
      state.createdAt = payload.createdAt;
      state.unread = payload.unread;
      state.notify = payload.notify;
    },

    updateUserNotificationState: (
      state,
      { payload }: PayloadAction<{ userId: string; data: Partial<PersistentUserNotificationState> }>
    ) => {
      const { userId, data } = payload;
      if (!state.userNotificationStates) {
        state.userNotificationStates = { [userId]: {} };
      }
      const current = state.userNotificationStates[userId];

      state.userNotificationStates[userId] = {
        ...current,
        ...data
      };
    }
  }
});

export default slice.reducer;

const {
  persistentDataRestored,
  getIslandProfileSucceeded,
  notificationNotified,
  refreshNotifications,
  notificationCenterOpened,
  ...internalActions
} = slice.actions;

export const actions = {
  persistentDataRestored,
  getIslandProfileSucceeded,
  notificationNotified,
  refreshNotifications,
  notificationCenterOpened
};

const { updateUserNotificationState, notificationsCreated } = internalActions;

// Saga

export function* notificationSaga() {
  yield takeLatest(notificationNotified.type, notificationNotifiedSaga);
  yield takeLatest(refreshNotifications.type, refreshNotificationsSaga);
  yield takeLatest(notificationCenterOpened.type, notificationCenterOpenedSaga);
}

export function* setupNotifications(userId: string) {
  const userState: UserState = yield select((state: RootState) => state.user);
  const { users, language, unlockFlags } = userState;

  const selectedUser = users.find(u => u.id === userId);
  if (!selectedUser || !language) {
    // 復帰不可能のエラー
    throw new Error();
  }

  const notificationState: NotificationState = yield select(
    (state: RootState) => state.notification
  );
  const { userNotificationStates, landProfile } = notificationState;

  if (!userId || !landProfile) {
    // 復帰不可能のエラー
    throw new Error();
  }

  const uns = (userNotificationStates || {})[userId] || {};
  const reactionAvailable = !!unlockFlags?.isDisplayEmoticon;
  const resortAvailable = !!unlockFlags?.isUnlockedResortAppNet;
  const lifeSupportsAvailable = !!unlockFlags?.isDisplayLifeSupport;

  yield call(
    createNotifications,
    userId,
    landProfile,
    uns,
    reactionAvailable,
    resortAvailable,
    lifeSupportsAvailable
  );
}

function* notificationNotifiedSaga() {
  const userState: UserState = yield select((state: RootState) => state.user);
  const { selectedUserId: userId } = userState;
  if (!userId) {
    return;
  }

  const notifiedTimestamp = Math.floor(Date.now() / 1000);

  try {
    // notifiedTimestampを永続化してからstoreを更新
    yield call(persistUserNotificationState, userId, { notifiedTimestamp });
    yield put(updateUserNotificationState({ userId, data: { notifiedTimestamp } }));
  } catch {}
}

function* refreshNotificationsSaga() {
  const userState: UserState = yield select((state: RootState) => state.user);
  const notificationState: NotificationState = yield select(
    (state: RootState) => state.notification
  );

  const { selectedUserId: userId, unlockFlags } = userState;
  const { landProfile, createdAt, userNotificationStates } = notificationState;

  if (!userId || !landProfile) {
    // 復帰不可能のエラー
    throw new Error();
  }

  // 通知を更新する必要があるかをチェック
  if (!shouldRefreshNotifications(createdAt)) {
    return;
  }

  const uns = (userNotificationStates || {})[userId] || {};
  const reactionAvailable = !!unlockFlags?.isDisplayEmoticon;
  const resortAvailable = !!unlockFlags?.isUnlockedResortAppNet;
  const lifeSupportsAvailable = !!unlockFlags?.isDisplayLifeSupport;

  yield call(
    createNotifications,
    userId,
    landProfile,
    uns,
    reactionAvailable,
    resortAvailable,
    lifeSupportsAvailable
  );
}

function* notificationCenterOpenedSaga() {
  const userState: UserState = yield select((state: RootState) => state.user);
  const notificationState: NotificationState = yield select(
    (state: RootState) => state.notification
  );

  const { selectedUserId: userId } = userState;
  const { landProfile, createdAt } = notificationState;

  if (!userId || !landProfile || !createdAt) {
    // 復帰不可能のエラー
    throw new Error();
  }

  const nowTimestamp = Math.floor(Date.now() / 1000);

  try {
    // readTimestampを永続化してからstoreを更新
    yield call(persistUserNotificationState, userId, { readTimestamp: nowTimestamp });
    yield put(updateUserNotificationState({ userId, data: { readTimestamp: nowTimestamp } }));
  } catch {}
}

function* createNotifications(
  userId: string,
  landProfile: LandProfile,
  userNotificationState: PersistentUserNotificationState,
  reactionAvailable: boolean,
  resortAvailable: boolean,
  lifeSupportsAvailable: boolean
) {
  let updatedValues: Partial<PersistentUserNotificationState> = {
    lastReactionStatus: reactionAvailable ? 'available' : 'unavailable',
    lastResortStatus: resortAvailable ? 'available' : 'unavailable',
    lastLifeSupportsStatus: lifeSupportsAvailable ? 'available' : 'unavailable'
  };
  const items: Notification[] = [];
  const nowTimestamp = Math.floor(Date.now() / 1000);

  if (shouldShowUpdateNotice(userNotificationState)) {
    let variant: number;
    if (resortAvailable && lifeSupportsAvailable) {
      variant = 0;
    } else if (resortAvailable && !lifeSupportsAvailable) {
      variant = 1;
    } else if (!resortAvailable && lifeSupportsAvailable) {
      variant = 2;
    } else {
      variant = 3;
    }

    items.push({ type: 'update', variant });

    updatedValues = {
      ...updatedValues,
      latestUpdateNotice: blancoVersion,
      latestUpdateNoticeVariant: variant,
      updateNoticeTimestamp: nowTimestamp
    };
  }

  // リアクション解禁通知
  if (shouldShowReactionAvailable(reactionAvailable, userNotificationState)) {
    items.push({ type: 'reaction-available' });

    if (!userNotificationState.reactionActivatedTimestamp) {
      updatedValues = {
        ...updatedValues,
        reactionActivatedTimestamp: nowTimestamp
      };
    }
  }

  // ツクッター解禁通知
  if (shouldShowResortAvailable(resortAvailable, userNotificationState)) {
    items.push({ type: 'resort-available' });

    if (!userNotificationState.resortActivatedTimestamp) {
      updatedValues = {
        ...updatedValues,
        resortActivatedTimestamp: nowTimestamp
      };
    }
  }

  // ライフサポート解禁通知
  if (shouldShowLifeSupportsAvailable(lifeSupportsAvailable, userNotificationState)) {
    items.push({ type: 'life-supports-available' });

    if (!userNotificationState.lifeSupportsActivatedTimestamp) {
      updatedValues = {
        ...updatedValues,
        lifeSupportsActivatedTimestamp: nowTimestamp
      };
    }
  }

  try {
    yield call(persistUserNotificationState, userId, updatedValues);
    yield put(
      updateUserNotificationState({
        userId,
        data: updatedValues
      })
    );
  } catch {}

  const dptMillis = getDailyProcessingDate().getTime();
  // 05:00AM以降なら誕生日通知を作る
  if (dptMillis < Date.now()) {
    const villagers = landProfile.mVillager;
    const npcs = landProfile.mNormalNpc;
    items.push(...createBirthdayNotifications(villagers, npcs, userId));
  }

  const unread = checkIfNotificationUnread(items, userNotificationState);
  const notify = items.length > 0 && shouldNotify(userNotificationState);

  yield put(notificationsCreated({ items, unread, notify, createdAt: nowTimestamp }));
}

// Blancoのアップデート通知を表示する必要があるかをチェックする関数
function shouldShowUpdateNotice(uns: PersistentUserNotificationState) {
  if (!uns.latestUpdateNotice) {
    return true;
  }
  // NOTE: 2.0.0、2.1.0、2.1.1からのアップデートの場合は通知しない
  if (
    uns.latestUpdateNotice === '2.0.0' ||
    uns.latestUpdateNotice === '2.1.0' ||
    uns.latestUpdateNotice === '2.1.1'
  ) {
    return false;
  }
  if (uns.latestUpdateNotice !== blancoVersion) {
    return true;
  }
  return !uns.updateNoticeTimestamp || isToday(new Date(uns.updateNoticeTimestamp * 1000));
}

// Blancoでリアクション機能解禁の通知を表示する必要がありかをチェックする関数
function shouldShowReactionAvailable(
  reactionAvailable: boolean,
  uns: PersistentUserNotificationState
) {
  if (uns.reactionActivatedTimestamp) {
    return isToday(new Date(uns.reactionActivatedTimestamp * 1000));
  } else {
    return reactionAvailable && uns.lastReactionStatus === 'unavailable';
  }
}

function shouldShowResortAvailable(resortAvailable: boolean, uns: PersistentUserNotificationState) {
  if (uns.resortActivatedTimestamp) {
    return isToday(new Date(uns.resortActivatedTimestamp * 1000));
  } else {
    return resortAvailable && uns.lastResortStatus === 'unavailable';
  }
}

function shouldShowLifeSupportsAvailable(
  lifeSupportsAvailable: boolean,
  uns: PersistentUserNotificationState
) {
  if (uns.lifeSupportsActivatedTimestamp) {
    return isToday(new Date(uns.lifeSupportsActivatedTimestamp * 1000));
  } else {
    return lifeSupportsAvailable && uns.lastLifeSupportsStatus === 'unavailable';
  }
}

// 通知リストを更新する必要があるかをチェックする関数
function shouldRefreshNotifications(createdAt?: number) {
  if (!createdAt) {
    return true;
  }

  const lastCreatedAt = new Date(createdAt * 1000);
  // 日付が変わっていた場合は更新
  if (!isToday(lastCreatedAt)) {
    return true;
  }

  // 05:00AM以降は誕生日通知を作る必要がある
  const dptMillis = getDailyProcessingDate().getTime();
  return dptMillis < Date.now() && dptMillis > lastCreatedAt.getTime();
}

function checkIfNotificationUnread(items: Notification[], state: PersistentUserNotificationState) {
  const {
    readTimestamp,
    reactionActivatedTimestamp,
    resortActivatedTimestamp,
    lifeSupportsActivatedTimestamp
  } = state;

  if (items.length === 0) {
    return false;
  }
  if (!readTimestamp) {
    return true;
  }

  if (items.find(item => item.type === 'reaction-available')) {
    return !reactionActivatedTimestamp || reactionActivatedTimestamp > readTimestamp;
  }

  if (items.find(item => item.type === 'resort-available')) {
    return !resortActivatedTimestamp || resortActivatedTimestamp > readTimestamp;
  }

  if (items.find(item => item.type === 'life-supports-available')) {
    return !lifeSupportsActivatedTimestamp || lifeSupportsActivatedTimestamp > readTimestamp;
  }

  const lastReadAt = new Date(readTimestamp * 1000);

  if (!isToday(lastReadAt)) {
    return true;
  }

  // 最後に通知をチェックした時刻が本日の05:00AM以前でかつ誕生日通知が作られている
  const dailyProcessingDate = getDailyProcessingDate();
  return (
    dailyProcessingDate.getTime() > lastReadAt.getTime() &&
    items.find(
      item =>
        item.type === 'birthday-today' ||
        item.type === 'birthday-today-leap' ||
        item.type === 'birthday-tomorrow'
    ) !== undefined
  );
}

// 通知カードを出す必要があるか
function shouldNotify(state?: PersistentUserNotificationState) {
  if (!state?.notifiedTimestamp) {
    return true;
  }
  // 通知カード最後に出したのが本日かどうか調べる
  const lastNotifiedDate = new Date(state.notifiedTimestamp * 1000);
  return !isToday(lastNotifiedDate);
}

function isToday(date: Date) {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}
